import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useAuth } from '../context/AuthContext';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import Combobox from '@/components/ui/Combobox';
import SearchableCombobox from '@/components/ui/SearchableCombobox';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/Card';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/Tabs';
import ConfirmDialog from '@/components/ui/ConfirmDialog';
import { 
  User, Mail, Briefcase, Phone, MapPin, Building, Award, Camera, Lock, Key, 
  Shield, UserPlus, Calendar, Upload, ArrowLeft, ArrowRight, CheckCircle, XCircle, Info as InfoIcon, Coffee, History
} from 'lucide-react';
import axios from 'axios';
import toast from 'react-hot-toast';
import useApi from '../hooks/useApi';

const Profile = () => {
  const { user: currentUser, checkAuth } = useAuth();
  const { id } = useParams();
  const navigate = useNavigate();
  const { get } = useApi();
  
  const [profileUser, setProfileUser] = useState(null);
  const [loading, setLoading] = useState(false);
  const [isOwnProfile, setIsOwnProfile] = useState(true);

  // Hierarchy Data
  const [wings, setWings] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [sections, setSections] = useState([]);
  const [offices, setOffices] = useState([]);

  const { register, handleSubmit, reset, setValue, watch } = useForm();
  
  // Register profilePicture field to ensure it's tracked
  useEffect(() => {
    register('profilePicture');
  }, [register]);

  const watchedWingId = watch('wingId');
  const watchedDepartmentId = watch('departmentId');
  
  // Password Change State
  const [passLoading, setPassLoading] = useState(false);
  const [passData, setPassData] = useState({ current: '', new: '', confirm: '' });
  const [adminPassLoading, setAdminPassLoading] = useState(false);
  const [adminPassData, setAdminPassData] = useState({ new: '', confirm: '' });

  // PIN Setup State
  const [pinLoading, setPinLoading] = useState(false);
  const [pinData, setPinData] = useState({ currentPass: '', pin: '', confirmPin: '' });
  const [showPinForm, setShowPinForm] = useState(false);
  const [uploading, setUploading] = useState(false);

  const [mfaLoading, setMfaLoading] = useState(false);
  const [mfaEnrollData, setMfaEnrollData] = useState(null);
  const [mfaCode, setMfaCode] = useState('');

  // Delegation State
  const [delegations, setDelegations] = useState([]);
  const [usersList, setUsersList] = useState([]);
  const [delegationLoading, setDelegationLoading] = useState(false);
  const [delegationData, setDelegationData] = useState({ delegateeId: '', startDate: '', endDate: '' });
  
  const [confirmDialog, setConfirmDialog] = useState({
    isOpen: false,
    title: '',
    message: '',
    confirmText: 'Confirm',
    variant: 'danger',
    isLoading: false,
    onConfirm: () => {}
  });

  useEffect(() => {
    fetchWings();
    fetchOffices();
  }, []);

  const fetchWings = async () => {
    try {
      const response = await get('/hierarchy/wings');
      if (response.success) setWings(response.data);
    } catch (error) {
      console.error('Failed to fetch wings', error);
    }
  };

  const handleLeaveStatusChange = async (isChecked) => {
    try {
        const token = localStorage.getItem('token');
        const response = await axios.patch(`/api/users/${profileUser.id}/leave-status`, {
            isOnLeave: isChecked,
            leaveReason: watch('leaveReason')
        }, {
            headers: { Authorization: `Bearer ${token}` }
        });
        
        if (response.data.success) {
            setValue('isOnLeave', isChecked);
            setProfileUser({ ...profileUser, isOnLeave: isChecked });
            toast.success(isChecked ? 'Marked as On Leave' : 'Marked as Available');
        }
    } catch (error) {
        console.error('Error updating leave status:', error);
        toast.error('Failed to update status');
        // Revert switch if failed
        setValue('isOnLeave', !isChecked);
    }
  };

  const saveLeaveNote = async () => {
    try {
        const token = localStorage.getItem('token');
        const response = await axios.patch(`/api/users/${profileUser.id}/leave-status`, {
            isOnLeave: watch('isOnLeave'),
            leaveReason: watch('leaveReason')
        }, {
            headers: { Authorization: `Bearer ${token}` }
        });
        
        if (response.data.success) {
            toast.success('Leave note updated');
        }
    } catch (error) {
        console.error('Error updating leave note:', error);
        toast.error('Failed to update note');
    }
  };

  const enrollMfaTotp = async () => {
    if (!profileUser?.id) return;
    try {
      setMfaLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.post(`/api/users/${profileUser.id}/mfa/totp/enroll`, {}, { headers: { Authorization: `Bearer ${token}` } });
      if (response.data?.success) {
        setMfaEnrollData(response.data.data);
        setMfaCode('');
        toast.success('MFA enrollment created. Verify code to enable.');
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to enroll MFA');
    } finally {
      setMfaLoading(false);
    }
  };

  const enableMfaTotp = async () => {
    if (!profileUser?.id) return;
    if (!mfaCode) {
      toast.error('Enter your 6-digit code');
      return;
    }
    try {
      setMfaLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.post(`/api/users/${profileUser.id}/mfa/totp/enable`, { code: mfaCode }, { headers: { Authorization: `Bearer ${token}` } });
      if (response.data?.success) {
        toast.success('MFA enabled');
        setMfaEnrollData(null);
        setMfaCode('');
        const updated = { ...profileUser, mfaTotpEnabled: 1 };
        setProfileUser(updated);
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to enable MFA');
    } finally {
      setMfaLoading(false);
    }
  };

  const disableMfaTotp = async () => {
    if (!profileUser?.id) return;
    if (!mfaCode && !pinData.currentPass) {
      toast.error('Enter your 6-digit code (or current password)');
      return;
    }
    try {
      setMfaLoading(true);
      const token = localStorage.getItem('token');
      const payload = mfaCode ? { code: mfaCode } : { currentPassword: pinData.currentPass };
      const response = await axios.post(`/api/users/${profileUser.id}/mfa/totp/disable`, payload, { headers: { Authorization: `Bearer ${token}` } });
      if (response.data?.success) {
        toast.success('MFA disabled');
        setMfaEnrollData(null);
        setMfaCode('');
        const updated = { ...profileUser, mfaTotpEnabled: 0 };
        setProfileUser(updated);
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to disable MFA');
    } finally {
      setMfaLoading(false);
    }
  };


  const fetchDepartments = async (wingId) => {
    if (!wingId) {
      setDepartments([]);
      return;
    }
    try {
      const response = await get(`/hierarchy/wings/${wingId}/departments`);
      if (response.success) setDepartments(response.data);
    } catch (error) {
      console.error('Failed to fetch departments', error);
    }
  };

  const fetchSections = async (departmentId) => {
    if (!departmentId) {
      setSections([]);
      return;
    }
    try {
      const response = await get(`/hierarchy/departments/${departmentId}/sections`);
      if (response.success) setSections(response.data);
    } catch (error) {
      console.error('Failed to fetch sections', error);
    }
  };

  const fetchOffices = async () => {
    try {
      const response = await get('/offices');
      if (response.success) setOffices(response.data);
    } catch (error) {
      console.error('Failed to fetch offices', error);
    }
  };

  // Watch for Wing/Department changes to fetch dependents
  useEffect(() => {
    if (watchedWingId) fetchDepartments(watchedWingId);
  }, [watchedWingId]);

  useEffect(() => {
    if (watchedDepartmentId) fetchSections(watchedDepartmentId);
  }, [watchedDepartmentId]);

  useEffect(() => {
    const fetchProfile = async () => {
      if (!currentUser) return;

      const targetUserId = id || currentUser.id || currentUser.userId;
      const currentUserId = currentUser.id || currentUser.userId;
      const isOwn = String(targetUserId) === String(currentUserId);
      setIsOwnProfile(isOwn);

      if (isOwn) {
        // Even for own profile, fetch fresh data to get latest status like isOnLeave
        try {
            const token = localStorage.getItem('token');
            // Add timestamp to prevent caching
            const response = await axios.get(`/api/users/${currentUserId}?t=${Date.now()}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            if (response.data.success) {
                const foundUser = response.data.data;
                if (foundUser) {
                    setProfileUser(foundUser);
                    reset({
                        name: foundUser.name,
                        email: foundUser.email,
                        phone: foundUser.phone,
                        designation: foundUser.designation,
                        wingId: foundUser.wingId,
                        departmentId: foundUser.departmentId,
                        sectionId: foundUser.sectionId,
                        officeId: foundUser.officeId,
                        role: foundUser.role,
                        scale: foundUser.scale,
                        officialAddress: foundUser.officialAddress,
                        profilePicture: foundUser.profilePicture,
                        isOnLeave: foundUser.isOnLeave === 1 || foundUser.isOnLeave === true,
                        leaveReason: foundUser.leaveReason
                    });
                } else {
                     // Fallback to currentUser context if fetch fails
                     setProfileUser(currentUser);
                     reset({ ...currentUser });
                }
            }
        } catch (error) {
            console.error('Error fetching own profile:', error);
            setProfileUser(currentUser);
            reset({ ...currentUser });
        }
        
        fetchDelegations();
        fetchUsers();
      } else {
        // Fetch other user's profile
        try {
          const token = localStorage.getItem('token');
          const response = await axios.get(`/api/users/${targetUserId}`, {
             headers: { Authorization: `Bearer ${token}` }
          });
          
          if (response.data.success) {
            const foundUser = response.data.data;
            if (foundUser) {
              setProfileUser(foundUser);
              reset({
                name: foundUser.name,
                email: foundUser.email,
                phone: foundUser.phone,
                designation: foundUser.designation,
                wingId: foundUser.wingId,
                departmentId: foundUser.departmentId,
                sectionId: foundUser.sectionId,
                officeId: foundUser.officeId,
                role: foundUser.role,
                scale: foundUser.scale,
                officialAddress: foundUser.officialAddress,
                profilePicture: foundUser.profilePicture,
                isOnLeave: foundUser.isOnLeave === 1 || foundUser.isOnLeave === true,
                leaveReason: foundUser.leaveReason
              });
            } else {
              toast.error('User not found');
              navigate('/users');
            }
          }
        } catch (error) {
          console.error('Error fetching user profile:', error);
          toast.error('Failed to load profile');
        }
      }
    };

    fetchProfile();
  }, [id, currentUser, reset, navigate]);

  const fetchDelegations = async () => {
    try {
        const token = localStorage.getItem('token');
        const response = await axios.get('/api/users/delegations', {
            headers: { Authorization: `Bearer ${token}` }
        });
        if (response.data.success) {
            setDelegations(response.data.data);
        }
    } catch (error) {
        console.error('Error fetching delegations:', error);
    }
  };

  const fetchUsers = async () => {
    try {
        const token = localStorage.getItem('token');
        const response = await axios.get('/api/users', {
            headers: { Authorization: `Bearer ${token}` }
        });
        if (response.data.success) {
            const currentUserId = currentUser.id || currentUser.userId;
            const others = response.data.data.filter(u => u.id !== currentUserId);
            setUsersList(others);
        }
    } catch (error) {
        console.error('Error fetching users:', error);
    }
  };

  const handleDelegate = async (e) => {
    e.preventDefault();
    if (!delegationData.delegateeId || !delegationData.startDate || !delegationData.endDate) {
        return toast.error('Please fill all fields');
    }
    setDelegationLoading(true);
    try {
        const token = localStorage.getItem('token');
        const response = await axios.post('/api/users/delegations', delegationData, {
            headers: { Authorization: `Bearer ${token}` }
        });
        if (response.data.success) {
            toast.success('Inbox delegated successfully');
            setDelegationData({ delegateeId: '', startDate: '', endDate: '' });
            fetchDelegations();
        }
    } catch (error) {
        console.error('Error delegating:', error);
        toast.error(error.response?.data?.message || 'Failed to delegate');
    } finally {
        setDelegationLoading(false);
    }
  };

  const handleRevoke = (id) => {
    setConfirmDialog({
      isOpen: true,
      title: 'Revoke Delegation',
      message: 'Are you sure you want to revoke this delegation?',
      confirmText: 'Revoke',
      variant: 'danger',
      isLoading: false,
      onConfirm: async () => {
        setConfirmDialog(prev => ({ ...prev, isLoading: true }));
        try {
            const token = localStorage.getItem('token');
            const response = await axios.delete(`/api/users/delegations/${id}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            if (response.data.success) {
                toast.success('Delegation revoked');
                fetchDelegations();
                setConfirmDialog(prev => ({ ...prev, isOpen: false, isLoading: false }));
            }
        } catch (error) {
            console.error('Error revoking delegation:', error);
            toast.error('Failed to revoke delegation');
            setConfirmDialog(prev => ({ ...prev, isLoading: false }));
        }
      }
    });
  };

  const onSubmit = async (data) => {
    if (!isOwnProfile && currentUser.role !== 'SuperAdmin') return;

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const userId = profileUser.id || profileUser.userId; 
      
      // Get names for display purposes if needed by backend
      const departmentName = departments.find(d => d.id === data.departmentId)?.name || data.department;

      // Remove profilePicture from data to prevent overwriting with old/form value
      // Profile picture is updated separately via handleFileChange
      const { profilePicture, ...updateData } = data;

      const response = await axios.put(`/api/users/${userId}`, {
        ...updateData,
        officeId: data.officeId || profileUser.officeId, 
        role: data.role || profileUser.role,
        department: departmentName
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        toast.success('Profile updated successfully');
        
        // Prepare new state using updateData (which excludes profilePicture to prevent overwriting with FileList/empty)
        // We preserve the existing profilePicture in state (which was updated by handleFileChange)
        const newProfileState = { ...profileUser, ...updateData, department: departmentName };
        
        if (isOwnProfile && typeof checkAuth === 'function') {
            await checkAuth(); 
            // Also update local state for immediate feedback
            setProfileUser(newProfileState);
        } else {
            setProfileUser(newProfileState);
        }
      }
    } catch (error) {
      console.error('Error updating profile:', error);
      toast.error(error.response?.data?.message || 'Failed to update profile');
    } finally {
      setLoading(false);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (passData.new !== passData.confirm) {
      return toast.error('New passwords do not match');
    }
    if (passData.new.length < 6) {
      return toast.error('Password must be at least 6 characters');
    }

    setPassLoading(true);
    try {
      const token = localStorage.getItem('token');
      const userId = profileUser.id || profileUser.userId;
      const response = await axios.put(`/api/users/${userId}/change-password`, {
        currentPassword: passData.current,
        newPassword: passData.new
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        toast.success('Password changed successfully');
        setPassData({ current: '', new: '', confirm: '' });
      }
    } catch (error) {
      console.error('Error changing password:', error);
      toast.error(error.response?.data?.message || 'Failed to change password');
    } finally {
      setPassLoading(false);
    }
  };

  const handleAdminSetPassword = async (e) => {
    e.preventDefault();
    if (adminPassData.new !== adminPassData.confirm) {
      return toast.error('New passwords do not match');
    }
    if (String(adminPassData.new || '').length < 6) {
      return toast.error('Password must be at least 6 characters');
    }
    if (currentUser?.role !== 'SuperAdmin' || isOwnProfile) {
      return toast.error('Access denied');
    }

    setAdminPassLoading(true);
    try {
      const token = localStorage.getItem('token');
      const userId = profileUser.id || profileUser.userId;
      const response = await axios.put(`/api/users/${userId}/set-password`, {
        newPassword: adminPassData.new
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        toast.success('Password updated');
        setAdminPassData({ new: '', confirm: '' });
      }
    } catch (error) {
      console.error('Error setting password:', error);
      toast.error(error.response?.data?.message || 'Failed to update password');
    } finally {
      setAdminPassLoading(false);
    }
  };

  const handleSetPin = async (e) => {
    e.preventDefault();
    if (pinData.pin !== pinData.confirmPin) {
      return toast.error('PINs do not match');
    }
    if (pinData.pin.length !== 4 || isNaN(pinData.pin)) {
      return toast.error('PIN must be a 4-digit number');
    }

    setPinLoading(true);
    try {
      const token = localStorage.getItem('token');
      const userId = profileUser.id || profileUser.userId;
      const response = await axios.put(`/api/users/${userId}/set-pin`, {
        currentPassword: pinData.currentPass,
        pin: pinData.pin
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        toast.success('e-Sign PIN set successfully');
        setPinData({ currentPass: '', pin: '', confirmPin: '' });
        setShowPinForm(false);
        setProfileUser(prev => ({ ...prev, hasESignPin: 1 }));
        if (isOwnProfile && typeof checkAuth === 'function') await checkAuth();
      }
    } catch (error) {
      console.error('Error setting PIN:', error);
      toast.error(error.response?.data?.message || 'Failed to set PIN');
    } finally {
      setPinLoading(false);
    }
  };

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!['image/jpeg', 'image/png'].includes(file.type)) {
        return toast.error('Only JPG and PNG images are allowed');
    }
    
    if (file.size > 5 * 1024 * 1024) {
        return toast.error('File size must be less than 5MB');
    }

    const formData = new FormData();
    formData.append('profilePicture', file);

    setUploading(true);
    try {
        const token = localStorage.getItem('token');
        const userId = profileUser.id || profileUser.userId;
        const response = await axios.post(`/api/users/${userId}/profile-picture`, formData, {
            headers: {
                Authorization: `Bearer ${token}`,
                'Content-Type': 'multipart/form-data'
            }
        });

        if (response.data.success) {
            toast.success('Profile picture updated');
            
            const newFilename = response.data.data.filename || response.data.data.profilePicture;

            // Immediately update form value to prevent race condition
            setValue('profilePicture', newFilename);

            // Update local state immediately for instant feedback
            setProfileUser(prev => ({ ...prev, profilePicture: newFilename }));

            if (isOwnProfile && typeof checkAuth === 'function') {
                await checkAuth();
            }
        }
    } catch (error) {
        console.error('Error uploading profile picture:', error);
        toast.error('Failed to upload profile picture');
    } finally {
        setUploading(false);
    }
  };

  const getProfilePictureUrl = () => {
    if (!profileUser?.profilePicture) return null;
    if (profileUser.profilePicture.startsWith('http')) return profileUser.profilePicture;
    
    const userId = profileUser.id || profileUser.userId;
    // Append timestamp to prevent caching issues if the image was just updated
    return `/api/users/${userId}/profile-picture?v=${profileUser.profilePicture}`;
  };

  const [imageError, setImageError] = useState(false);

  // Reset image error when profile picture changes
  useEffect(() => {
    setImageError(false);
  }, [profileUser?.profilePicture]);

  if (!profileUser) return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50/50 pb-12 font-sans">
      {/* Header Banner with Modern Gradient */}
      <div className="h-64 bg-gradient-to-r from-indigo-900 via-blue-800 to-indigo-900 w-full relative overflow-hidden">
        <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/cubes.png')] opacity-10"></div>
        <div className="absolute top-6 left-6 z-10">
            <Button 
                variant="ghost" 
                className="text-white hover:bg-white/10 hover:text-white border border-white/20 backdrop-blur-sm"
                onClick={() => navigate(-1)}
            >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Dashboard
            </Button>
        </div>
        <div className="absolute bottom-0 left-0 w-full h-24 bg-gradient-to-t from-gray-50/90 to-transparent"></div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 -mt-32 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
            
            {/* Sidebar / User Info Card */}
            <div className="lg:col-span-4 space-y-6">
                <Card className="overflow-hidden border-0 shadow-2xl ring-1 ring-gray-200/50 bg-white/80 backdrop-blur-md">
                    <CardContent className="pt-10 px-6 pb-8 flex flex-col items-center text-center">
                        <div className="relative mb-6 group">
                            <div className="w-40 h-40 rounded-full ring-4 ring-white shadow-xl overflow-hidden bg-gray-100 flex items-center justify-center relative">
                                {!imageError && profileUser?.profilePicture ? (
                                    <img 
                                        src={getProfilePictureUrl()} 
                                        alt="Profile" 
                                        className="w-full h-full object-cover" 
                                        onError={(e) => {
                                            console.error('Image load error:', e);
                                            setImageError(true);
                                        }}
                                    />
                                ) : (
                                    <div className="flex flex-col items-center justify-center text-gray-400 bg-gray-100 w-full h-full">
                                        <User className="w-16 h-16 mb-2" />
                                        <span className="text-xs font-medium">No Image</span>
                                    </div>
                                )}
                                
                                {/* Overlay for upload */}
                                {(isOwnProfile || currentUser.role === 'SuperAdmin') && (
                                    <div 
                                        className="absolute inset-0 bg-black/50 flex flex-col items-center justify-center opacity-0 group-hover:opacity-100 transition-all duration-300 cursor-pointer backdrop-blur-sm"
                                        onClick={() => document.getElementById('profile-upload').click()}
                                    >
                                        <Camera className="w-8 h-8 text-white mb-1" />
                                        <span className="text-white text-xs font-medium">Change Photo</span>
                                    </div>
                                )}
                            </div>
                            
                            {(isOwnProfile || currentUser.role === 'SuperAdmin') && (
                                <input 
                                    type="file" 
                                    id="profile-upload" 
                                    className="hidden" 
                                    accept="image/jpeg, image/png"
                                    onChange={handleFileChange}
                                />
                            )}
                            
                            {/* Online Status Indicator */}
                            <div className={`absolute bottom-2 right-2 w-5 h-5 rounded-full border-4 border-white ${profileUser.isActive ? 'bg-green-500' : 'bg-gray-400'}`} title={profileUser.isActive ? "Active" : "Inactive"}></div>
                        </div>

                        <h2 className="text-2xl font-bold text-gray-900 mb-1">{profileUser.name}</h2>
                        <p className="text-sm font-medium text-indigo-600 bg-indigo-50 px-3 py-1 rounded-full inline-block mb-4">
                            {profileUser.designation || 'No Designation'}
                        </p>
                        
                        <div className="w-full grid grid-cols-2 gap-4 mt-2 border-t border-gray-100 pt-6">
                            <div className="text-center" title="Your system access level">
                                <p className="text-xs text-gray-500 uppercase tracking-wide font-semibold">Role</p>
                                <p className="font-medium text-gray-900 mt-1">{profileUser.role}</p>
                            </div>
                            <div className="text-center border-l border-gray-100" title="Your Basic Pay Scale">
                                <p className="text-xs text-gray-500 uppercase tracking-wide font-semibold">Scale</p>
                                <p className="font-medium text-gray-900 mt-1">BPS-{profileUser.scale || 'N/A'}</p>
                            </div>
                        </div>

                        {/* e-Sign PIN Badge */}
                        <div className="w-full mt-6 pt-6 border-t border-gray-100">
                             {(profileUser.hasESignPin === 1 || profileUser.hasESignPin === true) ? (
                                <div className="bg-green-50 border border-green-200 rounded-lg p-3 flex items-center justify-between group hover:bg-green-100 transition-colors">
                                    <div className="flex items-center gap-3" title="You can digitally sign documents with your PIN">
                                        <div className="bg-green-100 p-2 rounded-full text-green-600 group-hover:bg-white transition-colors">
                                            <Shield className="h-5 w-5" />
                                        </div>
                                        <div className="text-left">
                                            <p className="text-sm font-bold text-green-800">e-Sign Enabled</p>
                                            <p className="text-xs text-green-600">PIN is configured</p>
                                        </div>
                                    </div>
                                    {isOwnProfile && (
                                        <Button 
                                            variant="ghost" 
                                            size="sm"
                                            onClick={() => setShowPinForm(true)}
                                            className="text-green-700 hover:text-green-800 hover:bg-green-200 h-8 text-xs"
                                        >
                                            Change
                                        </Button>
                                    )}
                                </div>
                            ) : (
                                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 flex items-center justify-between">
                                    <div className="flex items-center gap-3">
                                        <div className="bg-amber-100 p-2 rounded-full text-amber-600">
                                            <Lock className="h-5 w-5" />
                                        </div>
                                        <div className="text-left">
                                            <p className="text-sm font-bold text-amber-800">e-Sign Disabled</p>
                                            <p className="text-xs text-amber-600">PIN not configured</p>
                                        </div>
                                    </div>
                                    {isOwnProfile && (
                                        <Button 
                                            size="sm"
                                            onClick={() => setShowPinForm(true)}
                                            className="bg-amber-500 hover:bg-amber-600 text-white border-none h-8 text-xs"
                                        >
                                            Setup
                                        </Button>
                                    )}
                                </div>
                            )}
                        </div>
                    </CardContent>
                </Card>

                {/* Quick Stats or Info could go here */}
            </div>

            {/* Main Content Area */}
            <div className="lg:col-span-8">
                <div className="bg-white rounded-xl shadow-lg border border-gray-100 overflow-hidden min-h-[600px]">
                    <Tabs defaultValue="personal" className="w-full">
                        <div className="border-b border-gray-100 px-6 py-4 bg-gray-50/50">
                            <TabsList className="bg-white border border-gray-200 p-1 rounded-lg">
                                <TabsTrigger value="personal" className="data-[state=active]:bg-indigo-50 data-[state=active]:text-indigo-700">
                                    <User className="w-4 h-4 mr-2" />
                                    Personal Details
                                </TabsTrigger>
                                {isOwnProfile && (
                                    <>
                                        <TabsTrigger value="delegation" className="data-[state=active]:bg-indigo-50 data-[state=active]:text-indigo-700">
                                            <UserPlus className="w-4 h-4 mr-2" />
                                            Delegation
                                        </TabsTrigger>
                                        <TabsTrigger value="security" className="data-[state=active]:bg-indigo-50 data-[state=active]:text-indigo-700">
                                            <Shield className="w-4 h-4 mr-2" />
                                            Security
                                        </TabsTrigger>
                                    </>
                                )}
                            </TabsList>
                        </div>

                        <div className="p-6">
                            <TabsContent value="personal" className="mt-0 focus-visible:outline-none">
                                <div className="flex items-center justify-between mb-6">
                                    <div>
                                        <h3 className="text-lg font-semibold text-gray-900">Profile Information</h3>
                                        <p className="text-sm text-gray-500">Update your personal details and official information.</p>
                                    </div>
                                    {!isOwnProfile && currentUser.role !== 'SuperAdmin' && (
                                        <span className="text-xs text-orange-600 bg-orange-50 px-2 py-1 rounded">Read Only</span>
                                    )}
                                </div>
                                
                                <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
                                    <div className="space-y-8">
                                        {/* Identity Section */}
                                        <div>
                                            <h4 className="text-sm font-bold text-gray-500 uppercase tracking-wider mb-4 border-b border-gray-100 pb-2">Identity & Role</h4>
                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                                <div className="md:col-span-1">
                                                    <Input
                                                        label="Full Name"
                                                        {...register('name')}
                                                        icon={User}
                                                        disabled={!isOwnProfile && currentUser.role !== 'SuperAdmin'}
                                                    />
                                                </div>
                                                <div className="md:col-span-1">
                                                    <Input
                                                        label="Designation"
                                                        {...register('designation')}
                                                        icon={Briefcase}
                                                        disabled={!isOwnProfile && currentUser.role !== 'SuperAdmin'}
                                                    />
                                                </div>
                                                <div className="md:col-span-1">
                                                    <Input
                                                        label="Scale / BPS"
                                                        {...register('scale')}
                                                        icon={Award}
                                                        disabled={!isOwnProfile && currentUser.role !== 'SuperAdmin'}
                                                    />
                                                </div>
                                                {['SuperAdmin', 'Admin'].includes(currentUser.role) && (
                                                    <div className="md:col-span-1">
                                                        <label className="block text-sm font-medium text-gray-700 mb-1">System Role</label>
                                                        <SearchableCombobox
                                                            items={['SuperAdmin', 'Admin', 'Officer', 'Clerk'].map(r => ({ label: r, value: r }))}
                                                            value={watch('role')}
                                                            onChange={(val) => setValue('role', val)}
                                                            placeholder="Select Role"
                                                            disabled={currentUser.role !== 'SuperAdmin' && watch('role') === 'SuperAdmin'}
                                                        />
                                                    </div>
                                                )}
                                            </div>
                                        </div>

                                        {/* Organization Section */}
                                        <div>
                                            <h4 className="text-sm font-bold text-gray-500 uppercase tracking-wider mb-4 border-b border-gray-100 pb-2">Organization Structure</h4>
                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                                {currentUser.role === 'SuperAdmin' && (
                                                    <div className="md:col-span-2">
                                                        <label className="block text-sm font-medium text-gray-700 mb-1">Office</label>
                                                        <SearchableCombobox
                                                            items={offices.map(o => ({ label: o.name, value: o.id }))}
                                                            value={watch('officeId')}
                                                            onChange={(val) => setValue('officeId', val)}
                                                            placeholder="Select Office"
                                                        />
                                                    </div>
                                                )}
                                                <div className="col-span-1">
                                                    <label className="block text-sm font-medium text-gray-700 mb-1">Wing</label>
                                                    <SearchableCombobox
                                                        items={wings.map(w => ({ label: w.name, value: w.id }))}
                                                        value={watchedWingId}
                                                        onChange={(val) => {
                                                            setValue('wingId', val);
                                                            setValue('departmentId', ''); 
                                                            setValue('sectionId', '');
                                                        }}
                                                        disabled={!isOwnProfile && currentUser.role !== 'SuperAdmin'}
                                                        placeholder="Select Wing"
                                                    />
                                                </div>
                                                <div className="col-span-1">
                                                    <label className="block text-sm font-medium text-gray-700 mb-1">Department</label>
                                                    <SearchableCombobox
                                                        items={departments.map(d => ({ label: d.name, value: d.id }))}
                                                        value={watchedDepartmentId}
                                                        onChange={(val) => {
                                                            setValue('departmentId', val);
                                                            setValue('sectionId', '');
                                                        }}
                                                        disabled={(!watchedWingId) || (!isOwnProfile && currentUser.role !== 'SuperAdmin')}
                                                        placeholder="Select Department"
                                                    />
                                                </div>
                                                <div className="col-span-1">
                                                    <label className="block text-sm font-medium text-gray-700 mb-1">Section</label>
                                                    <SearchableCombobox
                                                        items={sections.map(s => ({ label: s.name, value: s.id }))}
                                                        value={watch('sectionId')}
                                                        onChange={(val) => setValue('sectionId', val)}
                                                        disabled={(!watchedDepartmentId) || (!isOwnProfile && currentUser.role !== 'SuperAdmin')}
                                                        placeholder="Select Section"
                                                    />
                                                </div>
                                            </div>
                                        </div>

                                        {/* Contact Section */}
                                        <div>
                                            <h4 className="text-sm font-bold text-gray-500 uppercase tracking-wider mb-4 border-b border-gray-100 pb-2">Contact Details</h4>
                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                                <div className="md:col-span-1">
                                                    <Input
                                                        label="Email Address"
                                                        {...register('email')}
                                                        icon={Mail}
                                                        type="email"
                                                        disabled={!isOwnProfile && currentUser.role !== 'SuperAdmin'}
                                                    />
                                                </div>
                                                <div className="md:col-span-1">
                                                    <Input
                                                        label="Phone Number"
                                                        {...register('phone')}
                                                        icon={Phone}
                                                        disabled={!isOwnProfile && currentUser.role !== 'SuperAdmin'}
                                                    />
                                                </div>
                                                <div className="md:col-span-2">
                                                    <Input
                                                        label="Official Address"
                                                        {...register('officialAddress')}
                                                        icon={MapPin}
                                                        disabled={!isOwnProfile && currentUser.role !== 'SuperAdmin'}
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    {(isOwnProfile || currentUser.role === 'SuperAdmin') && (
                                        <div className="flex justify-end pt-4 border-t border-gray-100">
                                            <Button type="submit" disabled={loading}>
                                                {loading ? 'Saving...' : 'Save Changes'}
                                            </Button>
                                        </div>
                                    )}
                                </form>
                            </TabsContent>

                            {isOwnProfile && (
                                <>
                                    <TabsContent value="delegation" className="mt-0 focus-visible:outline-none">
                                        <div className="flex items-center justify-between mb-6">
                                            <div>
                                                <h3 className="text-lg font-semibold text-gray-900">Inbox Delegation</h3>
                                                <p className="text-sm text-gray-500">Delegate your inbox access to another officer temporarily.</p>
                                            </div>
                                            <div className="bg-blue-50 text-blue-700 px-3 py-1 rounded-full text-xs font-medium border border-blue-100 flex items-center">
                                                <InfoIcon className="w-3 h-3 mr-1" />
                                                Auto-expires at midnight of End Date
                                            </div>
                                        </div>

                                        <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm mb-8 relative overflow-hidden">
                                            <div className="absolute top-0 left-0 w-1 h-full bg-indigo-500"></div>
                                            <h4 className="text-sm font-bold text-gray-500 uppercase tracking-wider mb-4 flex items-center">
                                                <UserPlus className="w-4 h-4 mr-2" />
                                                New Delegation
                                            </h4>
                                            
                                            <form onSubmit={handleDelegate} className="grid grid-cols-1 md:grid-cols-12 gap-6 items-end">
                                                <div className="md:col-span-4">
                                                    <label className="block text-sm font-medium text-gray-700 mb-1">Delegate To</label>
                                                    <Combobox
                                                        options={usersList.map(u => ({ 
                                                            label: `${u.name}${u.designation ? ` (${u.designation})` : ''}${u.isOnLeave ? ' (On Leave)' : ''}`, 
                                                            value: u.id 
                                                        }))}
                                                        value={delegationData.delegateeId}
                                                        onChange={(val) => setDelegationData({ ...delegationData, delegateeId: val })}
                                                        placeholder={usersList.length === 0 ? "No other users found" : "Select Officer"}
                                                        disabled={usersList.length === 0}
                                                    />
                                                </div>
                                                <div className="md:col-span-3">
                                                    <label className="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
                                                    <div className="relative">
                                                        <input
                                                            type="date"
                                                            className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent outline-none transition-all"
                                                            value={delegationData.startDate}
                                                            onChange={(e) => setDelegationData({ ...delegationData, startDate: e.target.value })}
                                                            min={new Date().toISOString().split('T')[0]}
                                                        />
                                                        <Calendar className="w-4 h-4 text-gray-400 absolute left-3 top-2.5" />
                                                    </div>
                                                </div>
                                                <div className="md:col-span-3">
                                                    <label className="block text-sm font-medium text-gray-700 mb-1">End Date</label>
                                                    <div className="relative">
                                                        <input
                                                            type="date"
                                                            className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent outline-none transition-all"
                                                            value={delegationData.endDate}
                                                            onChange={(e) => setDelegationData({ ...delegationData, endDate: e.target.value })}
                                                            min={delegationData.startDate || new Date().toISOString().split('T')[0]}
                                                        />
                                                        <Calendar className="w-4 h-4 text-gray-400 absolute left-3 top-2.5" />
                                                    </div>
                                                </div>
                                                <div className="md:col-span-2">
                                                    <Button type="submit" disabled={delegationLoading} className="w-full bg-indigo-600 hover:bg-indigo-700 text-white shadow-md hover:shadow-lg transition-all">
                                                        {delegationLoading ? (
                                                            <span className="flex items-center justify-center">
                                                                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                                                                Saving...
                                                            </span>
                                                        ) : 'Delegate'}
                                                    </Button>
                                                </div>
                                            </form>
                                        </div>

                                        <div className="mt-8 pt-6 border-t border-gray-100">
                                            <div className="flex items-center justify-between mb-4">
                                                <div>
                                                    <h3 className="text-lg font-semibold text-gray-900">Leave Status</h3>
                                                    <p className="text-sm text-gray-500">Mark yourself as on leave if you are not delegating your inbox.</p>
                                                </div>
                                                <div className="flex items-center gap-2">
                                                    <label className="relative inline-flex items-center cursor-pointer group">
                                                        <input 
                                                            type="checkbox" 
                                                            className="sr-only peer" 
                                                            checked={watch('isOnLeave') || false} 
                                                            onChange={(e) => handleLeaveStatusChange(e.target.checked)}
                                                        />
                                                        <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-indigo-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-indigo-600 group-hover:ring-2 group-hover:ring-indigo-100"></div>
                                                        <span className={`ml-3 text-sm font-medium transition-colors ${watch('isOnLeave') ? 'text-indigo-700 font-bold' : 'text-gray-600'}`}>
                                                            {watch('isOnLeave') ? 'Currently On Leave' : 'Available'}
                                                        </span>
                                                    </label>
                                                </div>
                                            </div>
                                            
                                            {watch('isOnLeave') && (
                                                <div className="bg-orange-50 border border-orange-100 rounded-xl p-6 transition-all animate-in fade-in slide-in-from-top-2 relative overflow-hidden">
                                                    <div className="absolute top-0 left-0 w-1 h-full bg-orange-400"></div>
                                                    <div className="flex gap-4">
                                                        <div className="bg-orange-100 p-2 rounded-full h-fit text-orange-600">
                                                            <Coffee className="w-5 h-5" />
                                                        </div>
                                                        <div className="flex-1">
                                                            <label className="block text-sm font-medium text-gray-800 mb-2">Leave Reason / Auto-reply Note</label>
                                                            <textarea
                                                                className="w-full px-4 py-3 border border-orange-200 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent outline-none transition-all bg-white text-gray-700 text-sm"
                                                                rows="3"
                                                                placeholder="e.g., On annual leave until 15th March. Contact Mr. X for urgent matters."
                                                                {...register('leaveReason')}
                                                            />
                                                            <div className="mt-3 flex justify-end">
                                                                <Button onClick={saveLeaveNote} size="sm" className="bg-orange-600 hover:bg-orange-700 text-white border-none shadow-sm">
                                                                    Save Note
                                                                </Button>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            )}
                                        </div>

                                        <div className="mt-10">
                                            <h4 className="text-md font-bold text-gray-900 mb-4 flex items-center">
                                                <History className="w-4 h-4 mr-2 text-gray-500" />
                                                Active & Scheduled Delegations
                                            </h4>
                                            <div className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm">
                                                {delegations.length > 0 ? (
                                                    <div className="overflow-x-auto">
                                                        <table className="min-w-full divide-y divide-gray-200">
                                                            <thead className="bg-gray-50/80">
                                                                <tr>
                                                                    <th className="px-6 py-4 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Delegated To</th>
                                                                    <th className="px-6 py-4 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Period</th>
                                                                    <th className="px-6 py-4 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Status</th>
                                                                    <th className="px-6 py-4 text-right text-xs font-bold text-gray-500 uppercase tracking-wider">Action</th>
                                                                </tr>
                                                            </thead>
                                                            <tbody className="bg-white divide-y divide-gray-200">
                                                                {delegations.map((del) => {
                                                                    const isActive = new Date() >= new Date(del.startDate) && new Date() <= new Date(del.endDate);
                                                                    return (
                                                                        <tr key={del.id} className="hover:bg-gray-50 transition-colors">
                                                                            <td className="px-6 py-4 whitespace-nowrap">
                                                                                <div className="flex items-center">
                                                                                    <div className="flex-shrink-0 h-8 w-8 rounded-full bg-indigo-100 flex items-center justify-center text-indigo-700 font-bold text-xs">
                                                                                        {del.delegateeName.charAt(0)}
                                                                                    </div>
                                                                                    <div className="ml-4">
                                                                                        <div className="text-sm font-medium text-gray-900">{del.delegateeName}</div>
                                                                                        <div className="text-xs text-gray-500">{del.delegateeDesignation || 'Officer'}</div>
                                                                                    </div>
                                                                                </div>
                                                                            </td>
                                                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                                                                                <div className="flex items-center gap-1">
                                                                                    <Calendar className="w-3 h-3 text-gray-400" />
                                                                                    {new Date(del.startDate).toLocaleDateString()} 
                                                                                    <ArrowRight className="w-3 h-3 text-gray-300 mx-1" />
                                                                                    {new Date(del.endDate).toLocaleDateString()}
                                                                                </div>
                                                                            </td>
                                                                            <td className="px-6 py-4 whitespace-nowrap">
                                                                                <span 
                                                                                    title={isActive ? "Delegation is currently active" : `Delegation will start on ${new Date(del.startDate).toLocaleDateString()}`}
                                                                                    className={`px-2.5 py-1 inline-flex text-xs leading-5 font-semibold rounded-full cursor-help ${
                                                                                    isActive
                                                                                    ? 'bg-green-100 text-green-800 border border-green-200'
                                                                                    : 'bg-yellow-100 text-yellow-800 border border-yellow-200'
                                                                                }`}>
                                                                                    {isActive ? 'Active Now' : 'Scheduled'}
                                                                                </span>
                                                                            </td>
                                                                            <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                                                                <button 
                                                                                    onClick={() => handleRevoke(del.id)} 
                                                                                    className="text-red-600 hover:text-red-900 bg-red-50 hover:bg-red-100 px-3 py-1 rounded-lg transition-colors text-xs font-semibold"
                                                                                >
                                                                                    Revoke Access
                                                                                </button>
                                                                            </td>
                                                                        </tr>
                                                                    );
                                                                })}
                                                            </tbody>
                                                        </table>
                                                    </div>
                                                ) : (
                                                    <div className="p-12 text-center flex flex-col items-center justify-center text-gray-500">
                                                        <div className="bg-gray-100 p-4 rounded-full mb-3">
                                                            <UserPlus className="w-8 h-8 text-gray-400" />
                                                        </div>
                                                        <p className="font-medium text-gray-900">No active delegations</p>
                                                        <p className="text-sm mt-1 max-w-xs mx-auto">You haven't delegated your inbox to anyone. Use the form above to set up a delegation.</p>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </TabsContent>

                                    <TabsContent value="security" className="mt-0 focus-visible:outline-none">
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                                            {/* Password Change */}
                                            <div>
                                                <div className="mb-6">
                                                    <h3 className="text-lg font-semibold text-gray-900">Change Password</h3>
                                                    <p className="text-sm text-gray-500">Ensure your account is using a strong password.</p>
                                                </div>
                                                {isOwnProfile ? (
                                                  <form onSubmit={handleChangePassword} className="space-y-4 bg-gray-50 p-6 rounded-lg border border-gray-100">
                                                      <Input
                                                          label="Current Password"
                                                          type="password"
                                                          value={passData.current}
                                                          onChange={(e) => setPassData({...passData, current: e.target.value})}
                                                          icon={Lock}
                                                          required
                                                      />
                                                      <Input
                                                          label="New Password"
                                                          type="password"
                                                          value={passData.new}
                                                          onChange={(e) => setPassData({...passData, new: e.target.value})}
                                                          icon={Key}
                                                          required
                                                          placeholder="Min. 6 characters"
                                                      />
                                                      <Input
                                                          label="Confirm New Password"
                                                          type="password"
                                                          value={passData.confirm}
                                                          onChange={(e) => setPassData({...passData, confirm: e.target.value})}
                                                          icon={Key}
                                                          required
                                                      />
                                                      <div className="pt-2">
                                                          <Button type="submit" disabled={passLoading} className="w-full">
                                                              {passLoading ? 'Updating...' : 'Update Password'}
                                                          </Button>
                                                      </div>
                                                  </form>
                                                ) : (currentUser?.role === 'SuperAdmin' ? (
                                                  <form onSubmit={handleAdminSetPassword} className="space-y-4 bg-gray-50 p-6 rounded-lg border border-gray-100">
                                                      <Input
                                                          label="New Password"
                                                          type="password"
                                                          value={adminPassData.new}
                                                          onChange={(e) => setAdminPassData({...adminPassData, new: e.target.value})}
                                                          icon={Key}
                                                          required
                                                          placeholder="Min. 6 characters"
                                                      />
                                                      <Input
                                                          label="Confirm New Password"
                                                          type="password"
                                                          value={adminPassData.confirm}
                                                          onChange={(e) => setAdminPassData({...adminPassData, confirm: e.target.value})}
                                                          icon={Key}
                                                          required
                                                      />
                                                      <div className="pt-2">
                                                          <Button type="submit" disabled={adminPassLoading} className="w-full">
                                                              {adminPassLoading ? 'Updating...' : 'Set Password'}
                                                          </Button>
                                                      </div>
                                                  </form>
                                                ) : (
                                                  <div className="bg-gray-50 p-6 rounded-lg border border-gray-100 text-sm text-gray-600">
                                                    Password can only be changed by the account owner (or SuperAdmin reset).
                                                  </div>
                                                ))}
                                            </div>

                                            {/* PIN Setup */}
                                            <div>
                                                <div className="mb-6 flex items-center justify-between">
                                                    <div>
                                                        <h3 className="text-lg font-semibold text-gray-900">e-Sign PIN</h3>
                                                        <p className="text-sm text-gray-500">Set up a 4-digit PIN for quick document signing.</p>
                                                    </div>
                                                    {(profileUser.hasESignPin === 1 || profileUser.hasESignPin === true) && !showPinForm && (
                                                        <div className="flex items-center gap-2 bg-green-100 text-green-700 px-3 py-1 rounded-full text-sm font-medium">
                                                            <CheckCircle className="h-4 w-4" />
                                                            e-Sign PIN Configured
                                                        </div>
                                                    )}
                                                </div>
                                                
                                                {(profileUser.hasESignPin === 1 || profileUser.hasESignPin === true) && !showPinForm ? (
                                                    <div className="bg-gray-50 p-6 rounded-lg border border-gray-100 flex flex-col items-center justify-center py-8">
                                                        <Shield className="h-12 w-12 text-green-600 mb-3 opacity-20" />
                                                        <p className="text-gray-600 mb-4 text-center">Your e-Sign PIN is currently active and secure.</p>
                                                        <Button 
                                                            variant="outline" 
                                                            onClick={() => setShowPinForm(true)}
                                                            className="border-gray-300 text-gray-700 hover:bg-white"
                                                        >
                                                            Change PIN
                                                        </Button>
                                                    </div>
                                                ) : (
                                                    <form onSubmit={handleSetPin} className="space-y-4 bg-gray-50 p-6 rounded-lg border border-gray-100">
                                                        {(profileUser.hasESignPin === 1 || profileUser.hasESignPin === true) && (
                                                            <div className="flex justify-end mb-2">
                                                                <Button 
                                                                    variant="ghost" 
                                                                    size="sm" 
                                                                    type="button"
                                                                    onClick={() => setShowPinForm(false)}
                                                                    className="text-gray-500 hover:text-gray-700"
                                                                >
                                                                    Cancel
                                                                </Button>
                                                            </div>
                                                        )}
                                                        <Input
                                                            label="Current Password (for verification)"
                                                            type="password"
                                                            value={pinData.currentPass}
                                                            onChange={(e) => setPinData({...pinData, currentPass: e.target.value})}
                                                            icon={Lock}
                                                            required
                                                        />
                                                        <div className="grid grid-cols-2 gap-4">
                                                            <Input
                                                                label="4-Digit PIN"
                                                                type="password"
                                                                maxLength={4}
                                                                value={pinData.pin}
                                                                onChange={(e) => setPinData({...pinData, pin: e.target.value})}
                                                                icon={Shield}
                                                                required
                                                                placeholder="XXXX"
                                                            />
                                                            <Input
                                                                label="Confirm PIN"
                                                                type="password"
                                                                maxLength={4}
                                                                value={pinData.confirmPin}
                                                                onChange={(e) => setPinData({...pinData, confirmPin: e.target.value})}
                                                                icon={Shield}
                                                                required
                                                                placeholder="XXXX"
                                                            />
                                                        </div>
                                                        <div className="pt-2">
                                                            <Button type="submit" disabled={pinLoading} variant="secondary" className="w-full">
                                                                {pinLoading ? 'Setting PIN...' : ((profileUser.hasESignPin === 1 || profileUser.hasESignPin === true) ? 'Update e-Sign PIN' : 'Set e-Sign PIN')}
                                                            </Button>
                                                        </div>
                                                    </form>
                                                )}
                                            </div>

                                            <div>
                                                <div className="mb-6 flex items-center justify-between">
                                                    <div>
                                                        <h3 className="text-lg font-semibold text-gray-900">Signing MFA (TOTP)</h3>
                                                        <p className="text-sm text-gray-500">Required for e-file signing actions.</p>
                                                    </div>
                                                    {(profileUser.mfaTotpEnabled === 1 || profileUser.mfaTotpEnabled === true) ? (
                                                        <div className="flex items-center gap-2 bg-green-100 text-green-700 px-3 py-1 rounded-full text-sm font-medium">
                                                            <CheckCircle className="h-4 w-4" />
                                                            MFA Enabled
                                                        </div>
                                                    ) : (
                                                        <div className="flex items-center gap-2 bg-amber-100 text-amber-800 px-3 py-1 rounded-full text-sm font-medium">
                                                            <InfoIcon className="h-4 w-4" />
                                                            MFA Required
                                                        </div>
                                                    )}
                                                </div>

                                                <div className="space-y-4 bg-gray-50 p-6 rounded-lg border border-gray-100">
                                                    {(profileUser.mfaTotpEnabled === 1 || profileUser.mfaTotpEnabled === true) ? (
                                                        <>
                                                            <Input
                                                                label="MFA Code"
                                                                type="text"
                                                                value={mfaCode}
                                                                onChange={(e) => setMfaCode(e.target.value)}
                                                                placeholder="Enter 6-digit code"
                                                                maxLength={6}
                                                                autoComplete="one-time-code"
                                                                inputMode="numeric"
                                                            />
                                                            <Button type="button" variant="outline" disabled={mfaLoading} onClick={disableMfaTotp} className="w-full">
                                                                {mfaLoading ? 'Working...' : 'Disable MFA'}
                                                            </Button>
                                                        </>
                                                    ) : (
                                                        <>
                                                            <Button type="button" disabled={mfaLoading} onClick={enrollMfaTotp} className="w-full">
                                                                {mfaLoading ? 'Generating...' : 'Enroll TOTP'}
                                                            </Button>
                                                            {mfaEnrollData?.qrDataUrl && (
                                                                <div className="bg-white border border-gray-200 rounded-lg p-4">
                                                                    <div className="flex items-start gap-4 flex-col md:flex-row">
                                                                        <img src={mfaEnrollData.qrDataUrl} alt="TOTP QR" className="w-40 h-40 border border-gray-100 rounded" />
                                                                        <div className="text-sm text-gray-700 space-y-2">
                                                                            <div className="font-medium">Scan QR in your authenticator app</div>
                                                                            <div className="text-gray-600 break-all">Secret: {mfaEnrollData.secretBase32}</div>
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            )}
                                                            <Input
                                                                label="Verify Code"
                                                                type="text"
                                                                value={mfaCode}
                                                                onChange={(e) => setMfaCode(e.target.value)}
                                                                placeholder="Enter 6-digit code"
                                                                maxLength={6}
                                                                autoComplete="one-time-code"
                                                                inputMode="numeric"
                                                            />
                                                            <Button type="button" variant="secondary" disabled={mfaLoading} onClick={enableMfaTotp} className="w-full">
                                                                {mfaLoading ? 'Verifying...' : 'Enable MFA'}
                                                            </Button>
                                                        </>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    </TabsContent>
                                </>
                            )}
                        </div>
                    </Tabs>
                </div>
            </div>
        </div>
      </div>
      <ConfirmDialog
        isOpen={confirmDialog.isOpen}
        onClose={() => setConfirmDialog(prev => ({ ...prev, isOpen: false }))}
        onConfirm={confirmDialog.onConfirm}
        title={confirmDialog.title}
        message={confirmDialog.message}
        confirmText={confirmDialog.confirmText}
        variant={confirmDialog.variant}
        isLoading={confirmDialog.isLoading}
      />
    </div>
  );
};

export default Profile;
